/**
 * Pure Unassigned row (SE-8391, Figma 355:3196 `molecules/UnassignedPlayerRow`):
 * grip · former-slot badge · name · "was {slot} · rank {n}" · GPR · Place ▾ ·
 * Remove. Visual parity NOT-VERIFIED until 944:16235 binds (M02).
 * @module features/scouting/components/molecules/PitchUnassignedRow
 */
import { GripVerticalIcon, Trash2Icon } from "lucide-react-native";
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { Dot } from "@/components/atoms/Dot";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PitchAssignmentSelect from "@/features/scouting/components/molecules/PitchAssignmentSelect";
import {
  PITCH_NAME_LINK_FOCUS_CLASS,
  PITCH_NAME_LINK_TEXT_CLASS,
} from "@/features/scouting/constants/pitchNameLink";
import type { PitchMemberRemoveState } from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import { formatPitchGpr } from "@/features/scouting/utils/watchlistPitchPresentation";

import type {
  PitchUnassignedRowProps,
  PitchUnassignedSurface,
} from "./PitchUnassignedRowContainer";

/** Prepared props for the pure row. */
export interface PitchUnassignedRowViewProps extends Omit<
  PitchUnassignedRowProps,
  "onRemove"
> {
  readonly nodeRef: (node: HTMLElement | null) => void;
  readonly dragBindings: Partial<React.ComponentProps<typeof Pressable>>;
  /** Bound Remove for this row; absent = no affordance. */
  readonly onRemove?: () => void;
  /** Opens this player's page (SE-8605); absent = the name is plain text. */
  readonly onOpenPlayer?: () => void;
}

/** What Remove takes the player out of, per surface. */
const REMOVE_TARGET: Readonly<Record<PitchUnassignedSurface, string>> = {
  watchlist: "the list",
  scenario: "the scenario",
};

/** Icon tone per removal state, as on the field chip. */
const REMOVE_TONE: Readonly<
  Record<PitchMemberRemoveState | "idle", "muted" | "danger" | "warning">
> = {
  idle: "muted",
  removing: "muted",
  failed: "danger",
  conflict: "warning",
};

/**
 * "was {slot} · rank {n}" when the former slot is known, "rank {n}" otherwise.
 * @param formerSlot - The slot held before the formation change, if known
 * @param rank - The member's list rank
 * @returns The row caption
 */
export const unassignedCaption = (
  formerSlot: string | null,
  rank: number
): string => (formerSlot ? `was ${formerSlot} · rank ${rank}` : `rank ${rank}`);

/**
 * Renders one Unassigned row.
 * @param root0 - Prepared row
 * @param root0.surface - Which pitch the panel sits under
 * @param root0.entry - The member's row data
 * @param root0.canDrag - Whether the grip is a drag source
 * @param root0.onPlace - Place ▾ write; absent disables the trigger
 * @param root0.nodeRef - DnD source node ref
 * @param root0.dragBindings - DnD activator attributes and listeners
 * @param root0.onRemove - Bound Remove, when offered
 * @param root0.removeState - Where that removal stands
 * @param root0.onOpenPlayer - Opens the player page from the name, when offered
 * @returns The rendered row
 */
const PitchUnassignedRowView = ({
  surface,
  entry,
  canDrag,
  onPlace,
  nodeRef,
  dragBindings,
  onRemove,
  removeState,
  onOpenPlayer,
}: PitchUnassignedRowViewProps) => (
  // SE-8486 (W80/S42, frame 971:20747): a borderless raised strip — px 12
  // py 8, gap 8, radius 8 — not the white bordered card it used to be.
  <Stack
    ref={nodeRef as never}
    testID={`pitch-unassigned-row-${entry.playerId}`}
    direction="horizontal"
    className="w-full items-center gap-2 rounded-md bg-surface-raised px-3 py-2"
  >
    {canDrag ? (
      <Pressable
        {...dragBindings}
        testID={`pitch-unassigned-drag-${entry.playerId}`}
        accessibilityRole="button"
        accessibilityLabel={`Move ${entry.displayName} from Unassigned`}
        className="size-5 touch-none items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:ring-outline-default"
      >
        <Icon as={GripVerticalIcon} size="xs" className="text-content-muted" />
      </Pressable>
    ) : (
      // The frame draws no marker (the grip stands there); a viewer's static
      // row keeps the Dot atom's own 8 px — the 6 px token is retired.
      <Dot
        testID={`pitch-unassigned-marker-${entry.playerId}`}
        className="bg-status-warning"
      />
    )}
    {/* W80: a bordered position chip — px 8 py 4, radius 4, caption-strong. */}
    <TonePill
      testID={`pitch-unassigned-slot-${entry.playerId}`}
      tone="neutral-outline"
      label={entry.formerSlot ?? "—"}
      textVariant="caption-strong"
      className="min-w-10 items-center rounded-sm px-2 py-1"
    />
    {/* W80: the name body-strong and `was LB · rank 1` as a muted caption
        on the SAME line — not a second micro line. */}
    <Stack
      testID={`pitch-unassigned-name-line-${entry.playerId}`}
      direction="horizontal"
      className="min-w-0 flex-1 items-center gap-2"
    >
      {onOpenPlayer ? (
        // SE-8605: the name opens the player page, as the Table view's does;
        // the grip keeps the drag and Place ▾ the assignment.
        <Pressable
          testID={`pitch-name-link-unassigned-${entry.playerId}`}
          accessibilityRole="link"
          onPress={onOpenPlayer}
          className={`min-w-0 shrink ${PITCH_NAME_LINK_FOCUS_CLASS}`}
        >
          <Text
            variant="body-strong"
            className={`text-content-primary ${PITCH_NAME_LINK_TEXT_CLASS}`}
            isTruncated
          >
            {entry.displayName}
          </Text>
        </Pressable>
      ) : (
        <Text
          variant="body-strong"
          className="min-w-0 shrink text-content-primary"
          isTruncated
        >
          {entry.displayName}
        </Text>
      )}
      <Text
        testID={`pitch-unassigned-caption-${entry.playerId}`}
        variant="caption"
        className="shrink-0 text-content-muted"
      >
        {unassignedCaption(entry.formerSlot, entry.rank)}
      </Text>
    </Stack>
    <Text
      testID={`pitch-unassigned-gpr-${entry.playerId}`}
      variant="micro"
      className="text-content-secondary"
    >
      GPR {formatPitchGpr(entry.gpr)}
    </Text>
    <PitchAssignmentSelect
      playerId={entry.playerId}
      surface="unassigned"
      playerName={entry.displayName}
      value={null}
      slots={entry.placeSlots}
      triggerLabel="Place"
      includeUnassigned={false}
      isDisabled={!onPlace || entry.placeSlots.length === 0}
      onAssign={onPlace ?? NOOP_PLACE}
    />
    {onRemove ? (
      <Button
        testID={`pitch-unassigned-remove-${entry.playerId}`}
        icon={Trash2Icon}
        iconOnly
        accessibilityLabel={`Remove ${entry.displayName} from ${REMOVE_TARGET[surface]}`}
        variant="link"
        size="xs"
        shape="rounded"
        iconSize="sm"
        iconTone={REMOVE_TONE[removeState ?? "idle"]}
        onPress={onRemove}
        isLoading={removeState === "removing"}
        isDisabled={removeState === "removing"}
      />
    ) : null}
  </Stack>
);

/** Stable no-op behind a disabled Place ▾. */
const NOOP_PLACE = (): void => undefined;

PitchUnassignedRowView.displayName = "PitchUnassignedRowView";
export default memo(PitchUnassignedRowView);
